// Thin, typed fetch client for the /api backend. Reads the entry token
// (set once from the /s/:token route) so every public request carries the
// x-entry-token header without every call site having to remember to.

import type {
  CheckoutHandoff,
  ContentAnswer,
  ContentCategory,
  ContentClarification,
  DropoffPoint,
  PublicRecipientStatus,
  RateQuote,
  RulesEvaluationResult,
  SelectedContent,
  SenderDetails,
  ShipmentIntentPublic,
  ShipmentPreferences,
} from "@shared/types";

let entryToken: string | null = null;
export function setEntryToken(token: string): void {
  entryToken = token;
}

class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("content-type", "application/json");
  if (entryToken) headers.set("x-entry-token", entryToken);

  const res = await fetch(`/api${path}`, { ...init, headers });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const message = body?.error?.message ?? "Something went wrong. Please try again.";
    throw new ApiClientError(res.status, body?.error?.code ?? "UNKNOWN", message);
  }
  return body as T;
}

export { ApiClientError };

export const api = {
  getRecipientStatus: () => request<PublicRecipientStatus>("/recipient/current"),

  createShipmentIntent: () => request<ShipmentIntentPublic>("/shipment-intents", { method: "POST" }),

  getShipmentIntent: (id: string) => request<ShipmentIntentPublic>(`/shipment-intents/${id}`),

  updateContents: (id: string, contents: SelectedContent[]) =>
    request<ShipmentIntentPublic>(`/shipment-intents/${id}/contents`, {
      method: "PATCH",
      body: JSON.stringify({ contents }),
    }),

  classifyContents: (id: string, description: string, clarifications: ContentClarification[] = []) =>
    request<{
      categories: ContentCategory[];
      answers: Partial<Record<ContentCategory, ContentAnswer>>;
      confidence: "high" | "medium" | "low";
      notes: string | null;
      clarifyingQuestion: { question: string; options: string[] | null } | null;
      usedLocalFallback: boolean;
    }>(`/shipment-intents/${id}/classify-contents`, {
      method: "POST",
      body: JSON.stringify({ description, clarifications }),
    }),

  updateParcel: (id: string, parcel: { weightGrams: number; lengthCm: number; widthCm: number; heightCm: number; valueGbp: number }) =>
    request<ShipmentIntentPublic>(`/shipment-intents/${id}/parcel`, { method: "PATCH", body: JSON.stringify(parcel) }),

  updateSender: (id: string, sender: SenderDetails) =>
    request<ShipmentIntentPublic>(`/shipment-intents/${id}/sender`, { method: "PATCH", body: JSON.stringify(sender) }),

  getRates: (id: string, preferences: ShipmentPreferences) =>
    request<{ shipment: ShipmentIntentPublic; quotes: RateQuote[]; rulesEvaluation: RulesEvaluationResult; unavailableProviders: string[] }>(
      `/shipment-intents/${id}/rates?priority=${encodeURIComponent(preferences.priority)}&printer=${encodeURIComponent(preferences.printerCapability)}`,
    ),

  selectQuote: (id: string, quote: { providerId: string; carrier: string; service: string }) =>
    request<ShipmentIntentPublic>(`/shipment-intents/${id}/select`, { method: "POST", body: JSON.stringify(quote) }),

  createCheckout: (id: string, confirmedHighValue = false) =>
    request<{ checkout: CheckoutHandoff }>(`/shipment-intents/${id}/checkout`, {
      method: "POST",
      body: JSON.stringify({ confirmedHighValue }),
    }),

  confirmPayment: (id: string, sessionId: string) =>
    request<{ shipment: ShipmentIntentPublic; paid: boolean }>(`/shipment-intents/${id}/confirm`, {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    }),

  pollPayment: (id: string) => request<ShipmentIntentPublic>(`/shipment-intents/${id}/poll-payment`, { method: "POST" }),

  searchDropoff: (postcode: string, carrier?: string) =>
    request<{ points: DropoffPoint[] }>(`/dropoff/search?postcode=${encodeURIComponent(postcode)}${carrier ? `&carrier=${carrier}` : ""}`),

  getTracking: (id: string) => request<{ tracking: unknown; live: boolean }>(`/tracking/${id}`),

  getSettings: () => request<{ tilePickerEnabled: boolean }>("/settings"),

  createSyncCode: (profile: { nickname: string; sender?: Record<string, string | undefined> }) =>
    request<{ code: string; expiresInSeconds: number }>("/device-sync", { method: "POST", body: JSON.stringify(profile) }),

  redeemSyncCode: (code: string) =>
    request<{ nickname: string; sender?: Record<string, string | undefined> }>("/device-sync/redeem", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
};
